import { useTheme } from "@emotion/react";
import useStyles from "./styles";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  BoltOutlined,
  DownloadOutlined,
  PublishOutlined,
  FlashOnOutlined,
  ThermostatOutlined,
  TimelineOutlined,
  VpnLockOutlined,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import CardGraphComponent from "../../components/cardGraphComponent";
import LoaderThreeDots from "../../components/loadingAnimation";
import CardDataComponent from "../../components/overview/cardDataComponent";
import LineChartCard from "./lineChartCard";
import InformationMale from "../artsPersonComponent/informationMale";
import ConfusedMale from "../artsPersonComponent/confusedMale";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

const INVERSED_POWER_RX =
  window?.config?.REACT_APP_INVERSED_POWER_RX ||
  process.env.REACT_APP_INVERSED_POWER_RX;

const DEFAULT_DATA = {
  onuCurrent: {
    currentValue: 0,
    data: [],
  },
  receivedPower: {
    currentValue: 0,
    data: [],
  },
  transmittedPower: {
    currentValue: 0,
    data: [],
  },
  onuTemperature: {
    currentValue: 0,
    data: [],
  },
  onuVoltage: {
    currentValue: 0,
    data: [],
  },
  onuTrafficIn: {
    currentValue: 0,
    data: [],
  },
  onuTrafficOut: {
    currentValue: 0,
    data: [],
  },
  firmwareVersion: {
    currentValue: "",
    data: [],
  },
  onuModel: {
    currentValue: "",
    data: [],
  },
  onuVlan: {
    currentValue: "",
    data: [],
  },
};

const HISTORY_METRICS = [
  "onuCurrent",
  "receivedPower",
  "transmittedPower",
  "onuTemperature",
  "onuVoltage",
  "onuTrafficIn",
  "onuTrafficOut",
];

// Métricas que precisam apenas do currentValue
const CURRENT_ONLY_METRICS = ["firmwareVersion", "onuModel", "onuVlan"];

// Todas as métricas disponíveis
const ALL_METRICS = [...HISTORY_METRICS, ...CURRENT_ONLY_METRICS];

export default function DashOnuComponent({
  filterData,

  handleAplicarZoom,
  setLoadingCronometro,
  setCounter,
  selectTime,
  optionsTime,
  startCronometro,
  controllerDashboard,
  setControllerDashboard,
  api,
  stopCronometro,
  availableItemsUrl,
  serieTemporalUrl,
  vlanFallbackUrl,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [hostRef, setHostRef] = useState(null);
  const [loading, setLoading] = useState({
    onuCurrent: false,
    receivedPower: false,
    transmittedPower: false,
    onuTemperature: false,
    onuVoltage: false,
    onuTrafficIn: false,
    onuTrafficOut: false,
    firmwareVersion: false,
    onuModel: false,
    onuVlan: false,
    threeDots: false,
  });
  const [data, setData] = useState(DEFAULT_DATA);
  const [availableMetrics, setAvailableMetrics] = useState([]);

  useEffect(() => {
    // if (
    //   filterData?.interfaceId &&
    //   filterData?.hostId &&
    //   filterData?.initialDate &&
    //   filterData?.finalDate
    // ) {
    //   if (filterData?.hostId !== hostRef) {
    //     setHostRef(filterData?.hostId);
    //     setData(DEFAULT_DATA);
    //     getMetricList();
    //   } else {
    //     fetchAllData();
    //   }
    // }
    getMetricList();
  }, [filterData]);

  useEffect(() => {
    if (
      filterData?.interfaceId &&
      filterData?.hostId &&
      filterData?.initialDate &&
      filterData?.finalDate &&
      availableMetrics?.length > 0
    ) {
      fetchAllData();
    }
  }, [availableMetrics]);

  useEffect(() => {
    return () => {
      if (controllerDashboard) {
        controllerDashboard?.abort();
      }
      stopCronometro();
    };
  }, []);

  const getMetricList = async () => {
    try {
      if (filterData?.device !== "onu") return;
      // setLoading({ ...loading, threeDots: true });
      if (!availableMetrics?.length > 0 || filterData?.hostId !== hostRef) {
        setLoading({ ...loading, threeDots: true });
        setHostRef(filterData?.hostId);
      }

      if (controllerDashboard) {
        controllerDashboard?.abort();
      }
      const newController = new AbortController();
      const signal = newController?.signal;
      setControllerDashboard(newController);

      const response = await api.post(
        availableItemsUrl,
        {
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
          dash: "onu",
        },
        { signal },
      );

      // Filtra apenas as métricas que existem na resposta
      const available = ALL_METRICS.filter((metric) =>
        response?.data?.includes(metric),
      );

      setAvailableMetrics(available);
    } catch (error) {
      console.error("Erro ao buscar lista de métricas:", error);
      setAvailableMetrics(ALL_METRICS); // Fallback para todas as métricas
    } finally {
      setLoading({ ...loading, threeDots: false });
    }
  };

  const fetchAllData = async () => {
    // Se não há métricas disponíveis, não faz nada
    if (availableMetrics?.length === 0) return;

    setCounter(null);
    stopCronometro();
    setLoadingCronometro(true);
    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;

    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime[selectTime]?.value;
      finalDate = dayjs()?.set("second", 0)?.set("millisecond", 0)?.toDate();
    }
    if (controllerDashboard) {
      controllerDashboard.abort();
    }
    const newController = new AbortController();
    const signal = newController.signal;
    setControllerDashboard(newController);

    const onuItem = filterData?.interfaceId?.split(" - ");

    const commonSearchParams = {
      hostids: filterData?.hostId,
      device: filterData?.device,
      //identificadores do schema
      fabricante: filterData?.fabricante,
      funcao: filterData?.funcao,
      modelo: filterData?.modelo,
      dash: "onu",
      serialOnu: onuItem?.[1]?.trim(),
      usernameCliente: onuItem?.[2]?.trim(),
      posicaoOnu: onuItem?.[0]?.trim(),
    };

    // Busca apenas as métricas disponíveis
    await Promise.all(
      availableMetrics?.map((metric) =>
        getMetric(metric, commonSearchParams, dateInitial, finalDate, signal),
      ),
    );

    if (!signal.aborted) {
      setLoadingCronometro(false);
      startCronometro();
    }
  };

  const getMetric = async (
    metric,
    commonSearchParams,
    dateInitial,
    finalDate,
    signal,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, [metric]: true }));

      if (CURRENT_ONLY_METRICS?.includes(metric)) {
        // Busca apenas o último valor
        const last = await api.post(
          serieTemporalUrl,
          {
            ...commonSearchParams,
            nameFilter: metric,
            final_date: finalDate,
            lastItem: true,
          },
          { signal },
        );

        // Exception para o caso da consulta ser vlan e não ter dados
        if (
          (metric === "onuVlan" && !last?.data?.value) ||
          last?.data?.value <= 0
        ) {
          const vlanFromCircuit = await api.post(
            vlanFallbackUrl,
            {
              ...commonSearchParams,
              serviceId: filterData?.serviceId,
              // O mesmo número de serviço pode existir em outro ERP: a VLAN
              // vem só dos circuitos deste cliente (ou dos membros do grupo).
              clienteId: filterData?.clienteId,
            },
            { signal },
          );

          const vlanId =
            vlanFromCircuit?.data?.find((item) =>
              item?.serial?.includes(commonSearchParams?.serialOnu),
            )?.vlanId || null;

          setData((prevData) => ({
            ...prevData,
            [metric]: {
              currentValue: vlanId,
              data: [],
            },
          }));

          return;
        }

        setData((prevData) => ({
          ...prevData,
          [metric]: {
            currentValue: last?.data?.value || "N/A",
            data: [],
          },
        }));
      } else {
        // Busca dados históricos + último valor (lógica original)
        const [history, last] = await Promise.all([
          api.post(
            serieTemporalUrl,
            {
              ...commonSearchParams,
              initial_date: dateInitial,
              final_date: finalDate,
              nameFilter: metric,
            },
            { signal },
          ),
          api.post(
            serieTemporalUrl,
            {
              ...commonSearchParams,
              nameFilter: metric,
              final_date: finalDate,
              lastItem: true,
            },
            { signal },
          ),
        ]);

        setData((prevData) => ({
          ...prevData,
          [metric]: {
            currentValue: last?.data?.value || 0,
            data: history?.data || [],
          },
        }));
      }
    } catch (error) {
      console.error(`Erro ao buscar métrica ${metric}:`, error);
    } finally {
      setLoading((prev) => ({ ...prev, [metric]: false }));
    }
  };

  // Array de itens para cards
  const cardItems = [
    {
      key: "receivedPower",
      title: "Potência recebida",
      valueUnit: "dBm",
      typeIcon: 9,
      cardTextColor: "chip.main.waitingReview",
      colorCard: "inputData",
      formatValue: (value) =>
        value <= 0 ? `${parseFloat(value).toFixed(2)} dBm` : null,
    },
    {
      key: "transmittedPower",
      title: "Potência transmitida",
      valueUnit: "dBm",
      typeIcon: 10,
      cardTextColor: "chip.main.inProgress",
      colorCard: "outputData",
      formatValue: (value) =>
        value ? `${parseFloat(value)?.toFixed(2)} dBm` : " - ",
    },
    {
      key: "firmwareVersion",
      title: "Versão do Firmware",
      typeIcon: 11,
      cardTextColor: "chip.main.waitingReview",
      colorCard: "speeData",
      formatValue: (value) => value || "N/A",
    },
    {
      key: "onuModel",
      title: "Modelo da ONU",
      typeIcon: 12,
      cardTextColor: "chip.main.waitingReview",
      colorCard: "inputData",
      formatValue: (value) => value || "N/A",
    },
    {
      key: "onuVlan",
      title: "VLAN da ONU",
      icon: VpnLockOutlined,
      typeIcon: 13,
      cardTextColor: "chip.main.waitingReview",
      colorCard: "statusUp",
      formatValue: (value) =>
        value != -1 && value != 0 ? value || "N/A" : "Não encontrada",
    },
  ]?.filter((item) => availableMetrics?.includes(item?.key));

  // Array de itens para gráficos
  const graphItems = [
    {
      key: "receivedPower",
      title: "Potência recebida",
      valueUnit: "dBm",
      icon: DownloadOutlined,
      minValue: INVERSED_POWER_RX ? 0 : -40,
      maxValue: INVERSED_POWER_RX ? 40 : 0,
      getColor: (value) => {
        return "blue";
      },
      renderType: "lineChart",
      formatDataArray: (arr) =>
        arr?.map((item) => ({
          ...item,
          value:
            item?.value <= 0
              ? INVERSED_POWER_RX
                ? item?.value
                  ? -item?.value
                  : null
                : item?.value
              : null,
        })),
      formatValue: (value) =>
        value ? `${INVERSED_POWER_RX ? -value : value}` : 0,
      getChipColor: (value) => {
        if (value <= -27) return "critical";
        if (value <= -25) return "high";
        return "success";
      },
      showChipStatus: true,
      getChipLabel: (value) => {
        if (value <= -27) return "Crítica";
        if (value <= -25) return "Atenção";
        return "Ok";
      },
    },
    {
      key: "transmittedPower",
      title: "Potência transmitida",
      valueUnit: "dBm",
      icon: PublishOutlined,
      minValue: 0,
      getColor: (value) => {
        return "orange";
      },
      renderType: "lineChart",
    },
    {
      key: "onuTrafficIn",
      title: "Tráfego de entrada",
      icon: TimelineOutlined,
      getColor: (value) => "green",
      formatValue: (value) => (value ? formatTraffic(value) : 0),
      hideProgress: true,
    },
    {
      key: "onuTrafficOut",
      title: "Tráfego de saída",
      icon: TimelineOutlined,
      getColor: (value) => "green",
      formatValue: (value) => (value ? formatTraffic(value) : 0),
      hideProgress: true,
    },
    {
      key: "onuCurrent",
      title: "Corrente",
      valueUnit: "mA",
      icon: BoltOutlined,
      getColor: (value) => {
        if (value === null || value === undefined) return "gray";
        const numericValue = parseFloat(value);
        if (numericValue > 20) return "red"; // muito acima do normal (possível sobrecarga)
        if (numericValue > 16) return "orange"; // leve sobrecorrente
        if (numericValue < 8) return "yellow"; // abaixo do esperado (queda ou falha parcial)
        if (numericValue < 5) return "red"; // corrente muito baixa (falha ou circuito aberto)
        return "green";
      },
      getProgressValue: (value) => {
        const numericValue = parseFloat(value);

        if (numericValue === null || numericValue === undefined) return 0;
        // Normaliza para porcentagem (0-100)
        if (numericValue > 25) return 100; // Máximo
        if (numericValue < 0) return 0; // Mínimo
        return (numericValue / 25) * 100; // 0-25mA -> 0-100%
      },
    },
    {
      key: "onuVoltage",
      title: "Tensão",
      valueUnit: "V",
      icon: FlashOnOutlined,
      getColor: (value) => {
        if (value > 3.5) return "red"; // acima do limite normal
        if (value > 3.4) return "orange"; // leve sobrevoltagem
        if (value < 3.0) return "yellow"; // subvoltagem leve
        if (value < 2.8) return "red"; // tensão crítica
        return "green";
      },
      getProgressValue: (value) => {
        const numericValue = parseFloat(value);

        if (numericValue === null || numericValue === undefined) return 0;
        // Normaliza para porcentagem (0-100)
        if (numericValue > 3.6) return 100; // Máximo
        if (numericValue < 2.5) return 0; // Mínimo
        return ((numericValue - 2.5) / (3.6 - 2.5)) * 100; // 2.5V-3.6V -> 0-100%
      },
    },
    {
      key: "onuTemperature",
      title: "Temperatura",
      valueUnit: "Cº",
      icon: ThermostatOutlined,
      getColor: (value) => {
        if (value > 60) return "red"; // superaquecimento
        if (value > 50) return "orange"; // alta temperatura
        if (value < 10) return "blue"; // frio extremo
        if (value < 20) return "lightblue"; // abaixo da faixa ideal
        return "green";
      },
      getProgressValue: (value) => {
        const numericValue = parseFloat(value);

        if (numericValue === null || numericValue === undefined) return 0;
        // Normaliza para porcentagem (0-100)
        if (numericValue > 80) return 100; // Máximo
        if (numericValue < 10) return 0; // Mínimo
        return ((numericValue + 10) / (80 + 10)) * 100;
      },
    },
  ]?.filter((item) => availableMetrics?.includes(item?.key));

  return (
    <Box sx={styles.container}>
      <Grid container spacing={1}>
        {loading?.threeDots && (
          <Grid item xs={12}>
            <LoaderThreeDots />
          </Grid>
        )}

        {!loading?.threeDots &&
          filterData?.hostId &&
          availableMetrics?.length === 0 && (
            <Grid item xs={12}>
              <ConfusedMale
                mainFill={theme.palette.secondary.dark}
                primaryCircleFill={theme.palette.secondary.main}
                highlightTitle={"Oops !"}
                subtitle={`Parece que não temos\nnenhuma métrica disponível\npara este dispositivo.`}
              />
            </Grid>
          )}

        {!loading?.threeDots && !filterData?.hostId && (
          <Grid item xs={12}>
            <InformationMale
              mainFill={theme.palette.secondary.dark}
              primaryCircleFill={theme.palette.secondary.main}
              secondaryCircleFill={theme.palette.secondary.light}
              title={"Você ainda não selecionou um"}
              highlight={"cliente"}
              subtitle={
                "As informações de ONUs aparecerão aqui assim\nque um cliente for escolhido."
              }
            />
          </Grid>
        )}

        {/* Cards Section */}
        {!loading?.threeDots && filterData?.hostId && cardItems?.length > 0 && (
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {cardItems?.map((item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={
                    Array.isArray(cardItems) && cardItems?.length > 0
                      ? 12 / parseInt(cardItems?.length)
                      : 2.4
                  }
                  key={item?.key}
                >
                  <CardDataComponent
                    cardTile={item?.title}
                    cardValue={
                      item.formatValue
                        ? item.formatValue(data?.[item.key]?.currentValue)
                        : data?.[item.key]?.currentValue
                    }
                    cardTextColor={item?.cardTextColor}
                    typeIcon={item?.typeIcon}
                    colorCard={item?.colorCard}
                    loading={loading?.[item?.key]}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        {/* Graphs Section */}
        {!loading?.threeDots &&
          filterData?.hostId &&
          graphItems?.map((item) => {
            const renderers = {
              lineChart: (
                <Grid item xs={12} sm={6} key={item?.key}>
                  <LineChartCard
                    // Props do container
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                    title={item?.title}
                    icon={item?.icon}
                    loading={loading?.[item?.key]}
                    getColor={item?.getColor}
                    currentValue={data?.[item?.key]?.currentValue}
                    chipColor={
                      item?.getChipColor &&
                      item?.getChipColor(data?.[item?.key]?.currentValue)
                    }
                    chipLabel={
                      item?.getChipLabel &&
                      item?.getChipLabel(data?.[item?.key]?.currentValue)
                    }
                    showChipStatus={item?.showChipStatus}
                    height="200px"
                    // Props do gráfico
                    data={data?.[item?.key]?.data}
                    maxValuebps={item?.maxValue}
                    minValuebps={item?.minValue}
                    formatDataArray={
                      item?.formatDataArray && item?.formatDataArray
                    }
                    customTooltip={(props) => (
                      <CustomTooltip
                        {...props}
                        valueUnit={item?.valueUnit}
                        title={item?.title}
                        formatValue={item?.formatValue}
                        color={
                          item?.getColor
                            ? item?.getColor(data?.[item?.key]?.currentValue)
                            : "green"
                        }
                      />
                    )}
                    mainLineColor={
                      theme.palette.cardGraphic.graphLineColor[
                        item?.getColor
                          ? item?.getColor(data?.[item?.key]?.currentValue)
                          : "green"
                      ]
                    }
                    mainColor={
                      theme.palette.cardGraphic.graphColor[
                        item?.getColor
                          ? item?.getColor(data?.[item?.key]?.currentValue)
                          : "green"
                      ]
                    }
                    yOptions={{
                      tickFormatter: (value) => {
                        if (item?.formatValue) {
                          return `${item?.formatValue(value)} ${
                            item?.valueUnit || ""
                          }`;
                        }
                        const numericValue = parseFloat(value);
                        if (isNaN(numericValue)) {
                          return `0 ${item?.valueUnit || ""}`;
                        }
                        return `${numericValue.toFixed(1)} ${
                          item?.valueUnit || ""
                        }`;
                      },
                    }}
                    xOptions={{
                      tickFormatter: (clock, index) =>
                        dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
                    }}
                  />
                </Grid>
              ),
              default: (
                <Grid item xs={12} sm={6} key={item?.key}>
                  <CardGraphComponent
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                    hideProgress={item?.hideProgress}
                    cardTitleOverrideStyles={{ fontSize: "18px" }}
                    loading={loading?.[item?.key]}
                    titleGraph={item?.title}
                    data={
                      Array.isArray(data?.[item?.key]?.data)
                        ? data?.[item?.key]?.data
                        : []
                    }
                    icon={item?.icon}
                    valueCard={
                      item?.formatValue
                        ? item?.formatValue(data?.[item.key]?.currentValue)
                        : data?.[item?.key]?.currentValue
                          ? `${parseFloat(
                              data?.[item?.key]?.currentValue,
                            )?.toFixed(2)} ${item?.valueUnit || ""}`
                          : " - "
                    }
                    tickFormatter={(value) => {
                      if (item?.formatValue) {
                        return item?.formatValue(value);
                      }
                      const numericValue = parseFloat(value);
                      if (isNaN(numericValue))
                        return `0 ${item?.valueUnit || ""}`;
                      return `${numericValue?.toFixed(1)} ${
                        item?.valueUnit || ""
                      }`;
                    }}
                    CustomTooltip={(props) => (
                      <CustomTooltip
                        {...props}
                        valueUnit={item?.valueUnit}
                        title={item?.title}
                        formatValue={item?.formatValue}
                        color={
                          item?.getColor
                            ? item?.getColor(data?.[item?.key]?.currentValue)
                            : "green"
                        }
                      />
                    )}
                    iconColorCard={
                      item?.getColor
                        ? item?.getColor(data?.[item?.key]?.currentValue)
                        : "green"
                    }
                    minValuebps={item?.minValue}
                    getProgressValue={
                      item?.getProgressValue
                        ? () =>
                            item?.getProgressValue(
                              data?.[item?.key]?.currentValue,
                            )
                        : null
                    }
                  />
                </Grid>
              ),
            };

            return renderers[item?.renderType] || renderers.default;
          })}
      </Grid>
    </Box>
  );
}

const CustomTooltip = ({
  active,
  payload,
  label,
  valueUnit = "",
  title = "",
  formatValue,
  color = "green", // Nova prop para receber a cor do gráfico
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  if (!active || !payload || payload.length === 0) return null;

  const { clock, value } = payload?.[0]?.payload;

  // Aplicar formatValue se disponível
  const formattedValue = formatValue ? formatValue(value) : value?.toFixed(2);

  return (
    <Box sx={styles.boxTooltip}>
      <Typography fontSize={"14px"}>
        {`Data: ${dayjs?.unix(clock)?.format("DD/MM/YY HH:mm")}`}
      </Typography>

      <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
        <Box
          sx={{
            ...styles.customTooltip,
            bgcolor: theme.palette.cardGraphic.graphColor[color], // Usa a cor passada como prop
          }}
        />
        <Typography fontSize={"14px"}>
          {title || "Valor"}: {formattedValue} {valueUnit}
        </Typography>
      </Stack>
    </Box>
  );
};

function formatTraffic(valueInBytesPerSecond) {
  const KB = 1000;
  const MB = KB * 1000;
  const GB = MB * 1000;

  const value = Number(valueInBytesPerSecond);

  if (value < MB) {
    return `${(value / KB).toFixed(1)} KB/s`;
  } else if (value < GB) {
    return `${(value / MB).toFixed(2)} MB/s`;
  } else {
    return `${(value / GB).toFixed(2)} GB/s`;
  }
}
